import { ALIGN_TYPE, DATA_VISUALIZATION_DEFAULTS } from "../constants";

import { BaseChart } from "./base-chart-model";
import { IChart, IChartData, IChartSeries } from "./interfaces";
import { formatNumber } from '../utils/data-format-helper';

export interface IChartCenterText {
  primaryLabel?: string;
  primaryLabelPositionY?: string;
  secondaryLabel?: string;
  secondaryLabelPositionY?: string;
}

export interface ICircularChart extends IChart {
  arcThickness?: number;
  radius?: number;
  centerText?: IChartCenterText;
  strokeColor?: string;
  formatNumbers? : boolean;
}

/********* Circular Chart Model **********************/
export class CircularChart extends BaseChart implements ICircularChart {
  public arcThickness!: number;
  public centerText?: IChartCenterText;
  public radius!: number;
  public strokeColor!: string;

  // custom chart props
  public percentageData: number[] = [];
  public totalValue!: number;
  public formatNumbers? : boolean = false;

  constructor(obj: ICircularChart) {
    super(obj);
    Object.assign(this, obj);
    this.arcThickness = obj.arcThickness || DATA_VISUALIZATION_DEFAULTS.ARC_THICKNESS;
    this.radius = obj.radius || DATA_VISUALIZATION_DEFAULTS.RADIUS;
    this.strokeColor = obj.strokeColor || DATA_VISUALIZATION_DEFAULTS.STROKE_COLOR;
    this._processData();
  }

  // Properties
  public get width(): number {
    return this.diameter;
  }

  public get height(): number {
    return this.diameter;
  }

  public get diameter(): number {
    return this.radius * 2;
  }

  public get startAngle(): number {
    return 0;
  }

  public get endAngle(): number {
    return 2 * Math.PI;
  }

  public get startX(): number {
    return this.width / 2;
  }

  public get startY(): number {
    return this.height / 2;
  }

  public get data(): IChartData[] {
    return this.series[0]?.data || [];
  }

  public get defaultSeries(): IChartSeries {
    return this.series[0] || {} as IChartSeries;
  }

  public get enableCenterText(): boolean {
    return !!this.centerText;
  }

  public get primaryCenterLabel(): string {
    // TODO: data type based formatting
    return this.centerText?.primaryLabel ?? `${this.defaultSeries?.valuePrefix || ''}${this.formatNumbers ? formatNumber(this.totalValue) : this.totalValue}${this.defaultSeries?.valueSuffix || ''}`;
  }

  public get primaryCenterLabelDy(): string {
    return this.centerText?.primaryLabelPositionY || '0.1em';
  }

  public get secondaryCenterLabel(): string | undefined {
    return this.centerText?.secondaryLabel;
  }

  public get secondaryCenterLabelDy(): string {
    return this.centerText?.secondaryLabelPositionY || '1.8em';
  }

  public get svgFlexOrder(): string {
    return this.legend?.align === ALIGN_TYPE.LEFT || this.legend?.align === ALIGN_TYPE.TOP ? '2' : '1';
  }

  public get legendFlexOrder(): string {
    return this.legend?.align === ALIGN_TYPE.LEFT || this.legend?.align === ALIGN_TYPE.TOP ? '1' : '2';
  }

  public get legendTotalValue(): string {
    // TODO: data type based formatting
    return this.legend?.title?.value || `${this.defaultSeries?.valuePrefix || ''}${this.formatNumbers ? formatNumber(this.totalValue) : this.totalValue}${this.defaultSeries?.valueSuffix || ''}`;
  }

  public get legendValueSeperator(): string {
    return this.legend?.valueSeperator || DATA_VISUALIZATION_DEFAULTS.LEGEND_VALUE_SEPERATOR;
  }

  public get showInColumn(): boolean {
    return this.legend?.align === ALIGN_TYPE.TOP || this.legend?.align === ALIGN_TYPE.BOTTOM;
  }

  // TODO: Circular chart specific; Extract as helper if needed for other type of charts.
  private _processData(): void {
    this.totalValue = this.data.reduce((pre, curr) => pre + curr.y, 0);
    this.percentageData = this.data.map(m => (m.y * 100 / this.totalValue));
  }
}

export class PieChart extends CircularChart {
  constructor(obj: ICircularChart) {
    super(obj);
  }

  // Disable center text for PIE by overriding the base class prop
  public get enableCenterText(): boolean {
    return false;
  }

  public get innerRadius(): number {
    return 0;
  }
}

export class DonutChart extends CircularChart {
  constructor(obj: ICircularChart) {
    super(obj);
  }

  public get innerRadius(): number {
    return this.radius - this.arcThickness;
  }
}


export class SemiCircleChart extends CircularChart {
  constructor(obj: ICircularChart) {
    super(obj);
  }

  public get innerRadius(): number {
    return this.radius - this.arcThickness;
  }

  public get height(): number {
    return this.radius;
  }

  public get startAngle(): number {
    return -(0.5 * Math.PI);
  }

  public get endAngle(): number {
    return 0.5 * Math.PI;
  }

  public get startY(): number {
    return this.height;
  }

  public get primaryCenterLabelDy(): string {
    return this.centerText?.primaryLabelPositionY || '-0.8em';
  }

  public get secondaryCenterLabelDy(): string {
    return this.centerText?.secondaryLabelPositionY || '-0.2em';
  }
}

